import { DateTime } from "luxon";
import i18n from "@/i18n";

import {
  getAllHttp,
  fetchAllTasksFromDG2Http,
  getTaskByUserTypeBetweenDate,
  getTaskCountByUserType,
  createTaskHttp,
  updateTaskHttp,
  deleteTaskHttp,
} from "../../services/planning/TaskServices.js";

const state = {
  tasks: [],
  tasksBetweenDatesForUser: [],
  selectedTask: null,
};

const getters = {
  getTasks() {
    return state.tasks;
  },
  getTasksByDatesForUser() {
    return state.tasksBetweenDatesForUser;
  },
  getSelectedTask() {
    return state.selectedTask;
  },
};

const mutations = {
  SET_TASKS: (state, payload) => (state.tasks = payload),
  SET_TASKS_BETWEEN: (state, payload) =>
    (state.tasksBetweenDatesForUser = payload),
  ADD_NEW_TASK: (state, payload) => state.tasks.push(payload),
  EDIT_TASK: (state, payload) => {
    let index = state.tasks.findIndex((l) => l.id === payload.id);
    if (index !== -1) state.tasks.splice(index, 1, payload);
  },
  REMOVE_TASK: (state, payload) => {
    let index = state.tasks.findIndex((l) => l.details.id === payload);
    if (index !== -1) state.tasks.splice(index, 1);
  },
  SET_SELECTED_TASK: (state, payload) => (state.selectedTask = payload),
};

const actions = {
  /**
   * This method will create a task Object.
   *
   * @param  {Vuex} {rootGetters} necessary if call getters from other stores
   * @param  {Object - taskDto} taskBack task received from the Rest API that we need to transform
   * @returns {Object - task Front-End} return a task that can be used by the calendar in the Front-End
   */
  async createTaskObject({ rootGetters }, taskBack) {
    let task = taskBack;

    const posY = await rootGetters["userModule/getOnUserDisplay"].findIndex(
      (u) => u.id === task.user.id
    );

    let taskObject = {
      y: posY,
      x: task.beginDate,
      id: "TASK" + task.id,
      title: task.title,
      userId: task.user.id,
      employee: task.user.fullName,
      dateEnd: task.endDate,
      duration: task.duration,
      display: true,
      color: "#edd1ff",
      slug: task.slug,
      details: task,
    };
    return taskObject;
  },

  /**
   * This method will load all tasks from the database
   *
   * @param  {vueX} {commit necessary if call commit inside the method
   * @param  {vueX} dispatch} necessary if call dispatch inside the method
   * @returns {} it is returning nothing because the response will be loaded in the store directly => commit("SET_TASKS", response.data)
   */
  async loadAllTasks({ commit }) {
    await getAllHttp()
      .then((response) => commit("SET_TASKS", response.data))
      .catch((error) => {
        return error;
      });
  },
  /**
   * This method will load Tasks present between a time interval depending on selected view (year,quarter, month, week)
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} rootGetters necessary if call getters from another store
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {} currentDate the date where the client is located in the calendar
   * @returns {} it is returning nothing because the response will be load in the store directly
   */
  async loadTasks({ rootGetters, dispatch }, currentDate) {
    let timePrediod = rootGetters["eventModule/getTimePeriod"];

    let dateStart = null;
    let dateEnd = null;

    if (currentDate != null || currentDate != undefined) {
      switch (timePrediod) {
        case "year":
          currentDate = DateTime.fromISO(currentDate).startOf("year");
          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate.plus({ year: 1 }).plus({ week: 1 }).toISODate();
          break;
        case "quarter":
          currentDate = DateTime.fromISO(currentDate).startOf("quarter");
          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate
            .plus({ quarter: 1 })
            .plus({ week: 1 })
            .toISODate();
          break;
        case "month":
          currentDate = DateTime.fromISO(currentDate).startOf("month");
          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate
            .plus({ month: 1 })
            .plus({ week: 1 })
            .toISODate();
          break;
        case "week":
          currentDate = DateTime.fromISO(currentDate).startOf("week");
          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate.plus({ week: 1 }).toISODate();
          break;
      }
    } else {
      switch (timePrediod) {
        case "year":
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("year")
            .plus({ year: 1 });

          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate.plus({ year: 1 }).plus({ week: 1 }).toISODate();
          break;

        case "quarter":
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("quarter")
            .plus({ quarter: 1 });

          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate
            .plus({ quarter: 1 })
            .plus({ week: 1 })
            .toISODate();
          break;

        case "month":
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("month")
            .plus({ month: 1 });
          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate
            .plus({ month: 1 })
            .plus({ week: 1 })
            .toISODate();
          break;

        case "week":
          currentDate = DateTime.fromISO(getters.getStartingDay)
            .startOf("week")
            .plus({ week: 1 });

          dateStart = currentDate.minus({ week: 1 }).toISODate();
          dateEnd = currentDate.plus({ week: 1 }).toISODate();
          break;
      }
    }

    let userType = rootGetters["userModule/getCalendar"];

    const promise = await getTaskByUserTypeBetweenDate(
      dateStart,
      dateEnd,
      userType
    );

    const promises = promise.data.map((t) => {
      return dispatch("createTaskObject", t);
    });

    state.tasks = await Promise.all(promises);
  },
  /**
   * This method will give the number of tasks depending of the calendar selecteced (trainer, salesperson, admin, etc)
   *
   * @param  {Vuex} {rootGetters} necessary if call getters from another store
   * @returns {int} it is returning the number of leave-periods depending of the calendar selecteced
   */
  async countTasks({ rootGetters }) {
    let nb = await getTaskCountByUserType(rootGetters["userModule/getCalendar"])
      .then((response) => {
        return response.data.nb;
      })
      .catch((error) => {
        return error;
      });

    return nb;
  },

  setTasksBetweenDatesForUser({ commit }, { dateStart, duration, userY }) {
    let start = DateTime.fromISO(dateStart);
    let end = start.plus({ days: duration });

    let result = [];

    state.tasks.filter((t) => {
      if (
        t.y === userY &&
        end.startOf("day") >= DateTime.fromISO(t.x) &&
        start.startOf("day") <= DateTime.fromISO(t.dateEnd)
      ) {
        result.push(t);
      }
    });

    commit("SET_TASKS_BETWEEN", result);
  },

  /**
   * This method will delete the task passed as parameter
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param {number} taskID - Id of the task that we want to delete
   * @returns - a message depending on whether it is succesfully deleted or not
   */
  async deleteTask({ commit, dispatch }, taskId) {
    await deleteTaskHttp(taskId)
      .then((response) => {
        if (response.status === 200) {
          commit("REMOVE_TASK", taskId);

          // TODO configure web socket for response
          //
          // let messageWS = {
          //   type: "DELETE",
          //   id: JSON.stringify(taskId),
          //   event: null,
          // };
          // dispatch(
          //   "websocketModule/sendMessageToWebsocket",
          //   { messageWS },
          //   { root: true }
          // );
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.taskDeleteSuccess", {
                id: taskId,
              }),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.taskDeleteError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },

  /**
   *
   * @param {*} task - The task we want to update
   */
  async editTask({ commit, dispatch }, task) {
    const editedTask = formateTaskDto(task);

    await updateTaskHttp(editedTask)
      .then((response) => {
        dispatch("createTaskObject", response.data).then((result) =>
          commit("EDIT_TASK", result)
        );
        // TODO configure web socket for response
        //
        // let messageWS = {
        //   type: "UPDATE",
        //   id: JSON.stringify(editedTask.id),
        //   event: null,
        // };
        // dispatch(
        //   "websocketModule/sendMessageToWebsocket",
        //   { messageWS },
        //   { root: true }
        // );
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.taskUpdatedSuccess", {
              id: editedTask.id,
            }),
            typeAlert: "success",
          },
          { root: true }
        );
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.taskUpdateError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  async createTask({ commit, dispatch }, task) {
    const editedTask = formateTaskDto(task);

    await createTaskHttp(editedTask)
      .then((response) => {
        dispatch("createTaskObject", response.data).then((result) =>
          commit("ADD_NEW_TASK", result)
        );
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.taskAddSuccess", {
              id: editedTask.id,
            }),
            typeAlert: "success",
          },
          { root: true }
        );
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.taskAddError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  async setSelectedTask({ commit }, taskId) {
    if (taskId) {
      let taskObject = state.tasks.filter((t) => t.details.id === taskId);
      commit("SET_SELECTED_TASK", taskObject[0]);
    } else {
      commit("SET_SELECTED_TASK", null);
    }
  },
};

function formateTaskDto(frontobject) {
  return {
    id: frontobject.id,
    taskIdDg2: frontobject.taskIdDg2,
    title: frontobject.title,
    intervention: frontobject.intervention,
    user: frontobject.user,
    slug: frontobject.slug,
    beginDate: frontobject.beginDate,
    endDate: frontobject.endDate,
    duration: frontobject.duration,
    version: frontobject.version,
  };
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
